// src/pages/trading-portal/TradeExecution/AIAgentRuleEditor.js
// Rule Editor for a No-LLM AI agent: the rules list and the step editor.
//
// A rule is calculated values plus ordered steps. Each step has conditions
// (formula, comparison, formula), an action, a quantity and a price; the first step
// whose conditions all pass decides the cycle. The backend checks every formula
// (AIAgentRuleEngine.py), so this component only edits text and shows what comes back.
//
// AIAgentPage renders the shared Variables / Position Sizing sidebar beside this and
// inserts a clicked variable through the ref (`insertVariable`).

import React, {
    useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, startTransition,
} from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Select,
    Chip,
    Skeleton,
    Alert,
    IconButton,
    Tooltip,
    Switch,
    CircularProgress,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

// What `activate` takes to put the agent back on the built-in Pure Code Router.
const ROUTER_REF = 'router';

const ACTION_LABELS = {
    BUY: 'Buy',
    SELL: 'Sell',
    SHORT: 'Short',
    COVER: 'Cover',
    HOLD: 'Hold',
    CLOSE_ALL: 'Close All',
    EMERGENCY_EXIT: 'Emergency Exit',
};
const SIZED_ACTIONS = ['BUY', 'SELL', 'SHORT', 'COVER'];
const FLATTEN_ACTIONS = ['CLOSE_ALL', 'EMERGENCY_EXIT'];
// Used until the first GET .../rules answers; the server's lists replace them.
const DEFAULT_VOCABULARY = {
    actions: Object.keys(ACTION_LABELS),
    operators: ['<', '<=', '>', '>=', '==', '!='],
    functions: ['IF', 'MIN', 'MAX', 'ABS', 'FLOOR', 'CEIL', 'ROUND', 'IFNA', 'ISNA'],
};
const DEFAULT_LIMITS = {
    values: 20, steps: 30, conditions: 10, formula_chars: 200, step_name_chars: 50, rule_name_chars: 120, notes_chars: 2000,
};

// A new rule only holds, so activating it before it is finished cannot trade.
const NEW_RULE_DEFINITION = {
    values: [],
    steps: [{
        name: 'Step 1',
        enabled: true,
        conditions: [{ left: 'current_price', op: '<', right: 'vwap_lower' }],
        action: 'HOLD',
        quantity: '',
        price: '',
        note: '',
    }],
};
const newStep = (number) => ({
    name: `Step ${number}`,
    enabled: true,
    conditions: [{ left: '', op: '<', right: '' }],
    action: 'HOLD',
    quantity: '',
    price: '',
    note: '',
});

// How a step did in "Test on live data".
const STEP_STATUS = {
    FIRED: { label: 'Fires', bgcolor: '#dcfce7', color: '#16a34a' },
    HELD: { label: 'Matched, not sent', bgcolor: '#fef3c7', color: '#b45309' },
    NOT_MET: { label: 'Not met', bgcolor: '#f1f5f9', color: '#64748b' },
    SKIPPED: { label: 'Not reached', bgcolor: '#f1f5f9', color: '#94a3b8' },
    OFF: { label: 'Off', bgcolor: '#f1f5f9', color: '#94a3b8' },
};
const CONDITION_RESULT = {
    PASS: { icon: MuiIcons.CheckCircle, color: '#16a34a', title: 'Passes on the live data' },
    FAIL: { icon: MuiIcons.Cancel, color: '#dc2626', title: 'Does not pass on the live data' },
    NA: { icon: MuiIcons.HelpOutline, color: '#d97706', title: 'A value is N/A, so this does not pass' },
};

const DISCARD_CHANGES = 'Discard the changes you have not saved?';

// Steps mounted at a time when a rule opens. A step is a couple of hundred components, and
// mounting all 13 of the built-in rule in one render froze the page for seconds.
const STEPS_PER_TICK = 2;

const clone = (value) => JSON.parse(JSON.stringify(value));

// ['steps', 2, 'conditions', 0, 'left'] -> 'steps[2].conditions[0].left', the form the
// server reports an error's place in.
const pathKey = (path) => path.reduce((key, part) => {
    if (typeof part === 'number') return `${key}[${part}]`;
    return key ? `${key}.${part}` : part;
}, '');

const showValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 9 });
    return String(value);
};

// One message per place; the first one the server reported for it wins.
const errorsByPath = (list) => {
    const out = {};
    (list || []).forEach((item) => {
        if (!(item.path in out)) out[item.path] = item.message;
    });
    return out;
};

const fieldSx = {
    '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontFamily, fontSize: '0.8rem' },
    '& .MuiFormHelperText-root': { fontFamily, fontSize: '0.68rem', mx: 0.5 },
};
const sectionTitleSx = { fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#64748b' };
const addButtonSx = { fontFamily, textTransform: 'none', fontSize: '0.75rem', color: ACCENT, alignSelf: 'flex-start' };
const rowIconSx = { p: 0.4, mt: 0.5, color: '#94a3b8', '&:hover': { color: '#ef4444' } };

// A copy of `target` with `value` at `path`, sharing everything off that path. The steps
// an edit does not touch keep their identity, which is what lets StepCard skip them.
const setIn = (target, path, value) => {
    if (path.length === 0) return value;
    const [head, ...rest] = path;
    const copy = Array.isArray(target) ? [...target] : { ...target };
    copy[head] = setIn(target == null ? undefined : target[head], rest, value);
    return copy;
};

// A textarea that grows with its text without measuring anything: an invisible copy of
// the text (the ::after) shares its grid cell and gives the cell its height.
//
// MUI's own growing textarea measures itself in a layout effect. A rule mounts over a
// hundred formula boxes at once, each measurement forced a layout of the whole page, and
// opening the built-in rule took more than five seconds.
const growingTextareaSx = {
    display: 'grid',
    flex: 1,
    minWidth: 0,
    '&::after': {
        content: 'attr(data-value) " "',
        visibility: 'hidden',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        font: 'inherit',
        letterSpacing: 'inherit',
        gridArea: '1 / 1 / 2 / 2',
    },
    '& > textarea': { gridArea: '1 / 1 / 2 / 2', overflow: 'hidden', overflowWrap: 'anywhere' },
};
const GrowingTextarea = forwardRef(({ value, ...other }, ref) => (
    <Box component="span" data-value={value} sx={growingTextareaSx}>
        <textarea ref={ref} value={value} {...other} />
    </Box>
));
GrowingTextarea.displayName = 'GrowingTextarea';
// Every MUI input injects its own copy of two global auto-fill keyframes when it mounts,
// and finds its place for them with a search of the whole document. A rule has well over
// a hundred inputs, so that made a rule slower to open the more conditions it had (20
// condition rows took 660 ms to mount in a development build, 2 took 40). None of these
// boxes is auto-filled, and the page's other inputs define the keyframes anyway.
const NO_GLOBAL_STYLES = { disableInjectingGlobalStyles: true };
const NO_GLOBAL_STYLES_READ_ONLY = { disableInjectingGlobalStyles: true, readOnly: true };
const GROWING_INPUT_PROPS = { ...NO_GLOBAL_STYLES, inputComponent: GrowingTextarea };
const GROWING_INPUT_PROPS_READ_ONLY = { ...NO_GLOBAL_STYLES_READ_ONLY, inputComponent: GrowingTextarea };

// A formula box. Module-level on purpose: defined inside the editor it would be a new
// component on every render and lose focus at each keystroke.
//
// It grows to show a long formula whole, which a one-line box cut off. It is memoised on
// plain props, so typing in one box re-renders that one: `onEdit` and `onFocusField` must
// be stable, and the box's size comes in as numbers, not as a new style object.
const FORMULA_FIELD_PROPS = [
    'label', 'value', 'placeholder', 'error', 'hint', 'readOnly', 'focused', 'maxLength',
    'singleLine', 'width', 'flex', 'minWidth', 'onEdit', 'onFocusField',
];
const FormulaField = React.memo(({
    path, label, value, placeholder, error, hint, readOnly, focused, maxLength,
    singleLine, width, flex, minWidth, onEdit, onFocusField,
}) => (
    <TextField
        size="small"
        label={label}
        value={value}
        placeholder={placeholder}
        error={Boolean(error)}
        helperText={error || hint || undefined}
        multiline={!singleLine}
        rows={singleLine ? undefined : 1}
        // A formula is one line of text: Enter and pasted line breaks become spaces.
        onChange={(e) => onEdit(path, e.target.value.replace(/[\r\n]+/g, ' '))}
        onFocus={(e) => onFocusField(path, e.target)}
        InputProps={singleLine
            ? (readOnly ? NO_GLOBAL_STYLES_READ_ONLY : NO_GLOBAL_STYLES)
            : (readOnly ? GROWING_INPUT_PROPS_READ_ONLY : GROWING_INPUT_PROPS)}
        inputProps={{ maxLength, spellCheck: false, autoComplete: 'off' }}
        sx={{
            ...fieldSx,
            width,
            flex,
            minWidth,
            '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.78rem' },
            // The app's stylesheet gives every one-line text input 1rem and 10px 15px. A
            // name beside a formula keeps the formula's size instead.
            '& input.MuiInputBase-input': { fontSize: '0.78rem', padding: '8.5px 14px' },
            '& .MuiOutlinedInput-root': focused && !readOnly ? { '& fieldset': { borderColor: ACCENT, borderWidth: 2 } } : {},
        }}
    />
), (previous, next) => pathKey(previous.path) === pathKey(next.path)
    && FORMULA_FIELD_PROPS.every((name) => previous[name] === next[name]));
FormulaField.displayName = 'FormulaField';

// One step of a rule. Memoised on its props, and `setField` keeps the identity of every
// step it does not touch, so a keystroke re-renders the step being edited and no other.
// A step is about 25 MUI components and the built-in rule has 13 of them.
const StepCard = React.memo(({
    step, index, count, errors, tested, readOnly, focusedKey, limits, vocabulary,
    onField, onRestructure, onFocusField,
}) => {
    const path = ['steps', index];
    const status = tested ? STEP_STATUS[tested.status] : null;
    const sized = SIZED_ACTIONS.includes(step.action);
    const conditions = step.conditions || [];
    const errorAt = (...parts) => errors[pathKey([...path, ...parts])];
    // Props of the formula box at `parts` inside this step.
    const formula = (parts, extra = {}) => {
        const fullPath = [...path, ...parts];
        const key = pathKey(fullPath);
        return {
            path: fullPath,
            value: parts.reduce((target, part) => (target == null ? '' : target[part]), step) ?? '',
            error: errors[key],
            readOnly,
            focused: focusedKey === key,
            maxLength: limits.formula_chars,
            onEdit: onField,
            onFocusField,
            ...extra,
        };
    };
    const move = (offset) => onRestructure((definition) => {
        const [moved] = definition.steps.splice(index, 1);
        definition.steps.splice(index + offset, 0, moved);
    });

    return (
        <Paper sx={{
            p: 1.5, mb: 1.5, borderRadius: '10px', boxShadow: 'none',
            border: `1px solid ${tested?.status === 'FIRED' ? '#22c55e' : tested?.status === 'HELD' ? '#f59e0b' : '#e2e8f0'}`,
            opacity: step.enabled === false ? 0.6 : 1,
        }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                <Chip label={index + 1} size="small" sx={{ mt: 0.75, fontFamily, fontWeight: 700, fontSize: '0.7rem', height: 22, minWidth: 28 }} />
                <TextField size="small" label="Step name" value={step.name || ''}
                    error={Boolean(errorAt('name'))} helperText={errorAt('name')}
                    onChange={(e) => onField([...path, 'name'], e.target.value)}
                    InputProps={readOnly ? NO_GLOBAL_STYLES_READ_ONLY : NO_GLOBAL_STYLES}
                    inputProps={{ maxLength: limits.step_name_chars }}
                    sx={{ ...fieldSx, flex: 1, minWidth: 140 }} />
                {status && (
                    <Tooltip title={tested.detail || (tested.status === 'SKIPPED' && tested.would_pass
                        ? 'Its conditions pass, but an earlier step decided first' : '')}>
                        <Chip label={status.label} size="small"
                            sx={{ mt: 0.75, bgcolor: status.bgcolor, color: status.color, fontFamily, fontWeight: 600, fontSize: '0.68rem', height: 22 }} />
                    </Tooltip>
                )}
                {/* Native `title` hints on this card's buttons, not MUI Tooltips: a rule has a
                    few per step and one per condition, and each Tooltip cost as much to mount
                    as the button it wrapped. */}
                <Switch size="small" checked={step.enabled !== false} disabled={readOnly} sx={{ mt: 0.5 }}
                    title={step.enabled === false ? 'Off: this step is skipped' : 'On'}
                    inputProps={{ 'aria-label': `Step ${index + 1} on` }}
                    onChange={(e) => onField([...path, 'enabled'], e.target.checked)} />
                {!readOnly && (
                    <Box sx={{ display: 'flex', mt: 0.25 }}>
                        <IconButton size="small" disabled={index === 0} onClick={() => move(-1)}
                            title="Move up" aria-label={`Move step ${index + 1} up`}>
                            <MuiIcons.ArrowUpward sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <IconButton size="small" disabled={index === count - 1} onClick={() => move(1)}
                            title="Move down" aria-label={`Move step ${index + 1} down`}>
                            <MuiIcons.ArrowDownward sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <IconButton size="small" disabled={count <= 1}
                            title="Remove step" aria-label={`Remove step ${index + 1}`}
                            onClick={() => onRestructure((definition) => { definition.steps.splice(index, 1); })}
                            sx={{ '&:hover': { color: '#ef4444' } }}>
                            <MuiIcons.DeleteOutline sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Box>
                )}
            </Box>

            <Typography sx={{ ...sectionTitleSx, fontSize: '0.72rem', mb: 0.75 }}>When all of these pass</Typography>
            {errorAt('conditions') && (
                <Typography sx={{ fontFamily, fontSize: '0.68rem', color: '#d32f2f', mb: 0.5 }}>{errorAt('conditions')}</Typography>
            )}
            {conditions.map((condition, conditionIndex) => {
                const testedCondition = tested?.conditions?.[conditionIndex];
                const result = testedCondition ? CONDITION_RESULT[testedCondition.result] : null;
                const ResultIcon = result?.icon;
                const rowError = errorAt('conditions', conditionIndex) || errorAt('conditions', conditionIndex, 'op');
                return (
                    // Keyed by position: a row has no id, and rows are only added, removed or moved whole.
                    <Box key={conditionIndex} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ width: 20, mt: 1, flexShrink: 0 }}>
                                {ResultIcon && (
                                    <Tooltip title={result.title}>
                                        <ResultIcon sx={{ fontSize: '1.1rem', color: result.color }} />
                                    </Tooltip>
                                )}
                            </Box>
                            <FormulaField placeholder="current_price" {...formula(['conditions', conditionIndex, 'left'])}
                                hint={testedCondition ? `= ${showValue(testedCondition.left_value)}` : undefined} flex={1} />
                            {/* A native select: a rule has one per condition, and it needs no menu. */}
                            <Select native size="small" value={condition.op || ''} disabled={readOnly}
                                disableInjectingGlobalStyles
                                inputProps={{ 'aria-label': 'Comparison' }}
                                onChange={(e) => onField([...path, 'conditions', conditionIndex, 'op'], e.target.value)}
                                sx={{ width: 78, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {vocabulary.operators.map((op) => <option key={op} value={op}>{op}</option>)}
                            </Select>
                            <FormulaField placeholder="vwap_lower" {...formula(['conditions', conditionIndex, 'right'])}
                                hint={testedCondition ? `= ${showValue(testedCondition.right_value)}` : undefined} flex={1} />
                            {!readOnly && (
                                <IconButton size="small" sx={rowIconSx}
                                    title="Remove condition" aria-label={`Remove condition ${conditionIndex + 1}`}
                                    onClick={() => onRestructure((definition) => {
                                        definition.steps[index].conditions.splice(conditionIndex, 1);
                                    })}>
                                    <MuiIcons.Close sx={{ fontSize: '1rem' }} />
                                </IconButton>
                            )}
                        </Box>
                        {rowError && (
                            <Typography sx={{ fontFamily, fontSize: '0.68rem', color: '#d32f2f', ml: 3.5, mt: 0.25 }}>{rowError}</Typography>
                        )}
                    </Box>
                );
            })}
            {!readOnly && (
                <Button size="small" startIcon={<MuiIcons.Add sx={{ fontSize: '0.9rem' }} />} sx={addButtonSx}
                    disabled={conditions.length >= limits.conditions}
                    onClick={() => onRestructure((definition) => {
                        definition.steps[index].conditions = [
                            ...(definition.steps[index].conditions || []), { left: '', op: '<', right: '' },
                        ];
                    })}>
                    Add condition
                </Button>
            )}

            <Typography sx={{ ...sectionTitleSx, fontSize: '0.72rem', mt: 1, mb: 0.75 }}>Then</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Select native size="small" value={vocabulary.actions.includes(step.action) ? step.action : ''} disabled={readOnly}
                    disableInjectingGlobalStyles error={Boolean(errorAt('action'))}
                    inputProps={{ 'aria-label': 'Action' }}
                    onChange={(e) => onField([...path, 'action'], e.target.value)}
                    sx={{ width: 170, fontFamily, fontSize: '0.8rem' }}>
                    {!vocabulary.actions.includes(step.action) && <option value="" />}
                    {vocabulary.actions.map((action) => (
                        <option key={action} value={action}>{ACTION_LABELS[action] || action}</option>
                    ))}
                </Select>
                {sized ? (
                    <>
                        <FormulaField label="Quantity" placeholder="sizing_t1_shares"
                            {...formula(['quantity'])} flex={1} minWidth={160} />
                        <FormulaField label="Price" placeholder="Blank = market order"
                            {...formula(['price'])} flex={1} minWidth={160} />
                    </>
                ) : (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', mt: 1 }}>
                        {FLATTEN_ACTIONS.includes(step.action)
                            ? 'Closes the whole position at the market.'
                            : 'No order. Nothing below this step is checked in that cycle.'}
                    </Typography>
                )}
            </Box>
            {tested?.detail && (
                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#b45309', mt: 0.75 }}>
                    Not sent: {tested.detail}.
                </Typography>
            )}
            {(!readOnly || step.note) && (
                <TextField size="small" fullWidth label="Note (shown in the Decision Log)" value={step.note || ''}
                    onChange={(e) => onField([...path, 'note'], e.target.value)}
                    InputProps={readOnly ? NO_GLOBAL_STYLES_READ_ONLY : NO_GLOBAL_STYLES}
                    inputProps={{ maxLength: 300 }}
                    // Smaller than the app's 1rem inputs: a note must not outweigh the conditions above it.
                    sx={{ ...fieldSx, mt: 1.25, '& input.MuiInputBase-input': { fontSize: '0.78rem', padding: '8.5px 14px' } }} />
            )}
        </Paper>
    );
});
StepCard.displayName = 'StepCard';

const AIAgentRuleEditor = forwardRef(({
    agent, canAdd, canEdit, canDelete, onAgentUpdated, onNotify, onCatalogLoaded,
}, ref) => {
    const agentId = agent?.id;
    const [rules, setRules] = useState([]);
    const [shared, setShared] = useState([]);
    // undefined until the first load; null while the built-in router decides.
    const [activeRule, setActiveRule] = useState(undefined);
    const [vocabulary, setVocabulary] = useState(DEFAULT_VOCABULARY);
    const [limits, setLimits] = useState(DEFAULT_LIMITS);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    // The editable copy of the selected rule: { name, notes, definition }.
    const [draft, setDraft] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [errors, setErrors] = useState({});
    const [test, setTest] = useState(null);
    const [testing, setTesting] = useState(false);
    // The formula box a clicked variable goes into: { key, path }.
    const [focused, setFocused] = useState(null);
    const focusedInputRef = useRef(null);
    // How many of the open rule's steps are on screen so far (see STEPS_PER_TICK).
    const [shownSteps, setShownSteps] = useState(STEPS_PER_TICK);
    // The agent and rule on screen; a slow response for another one is dropped.
    const agentIdRef = useRef(agentId);
    const selectedIdRef = useRef(null);
    // Read through a ref so a parent passing a new function each render cannot
    // restart the load below.
    const onCatalogLoadedRef = useRef(onCatalogLoaded);
    onCatalogLoadedRef.current = onCatalogLoaded;

    const api = `/api/trade-execution/ai-agents/${agentId}/rules`;
    const selected = rules.find((rule) => rule.id === selectedId) || null;
    const readOnly = !selected || selected.is_system || !canEdit;

    const openRule = useCallback((rule) => {
        // Another rule starts again from its first steps. The same rule, reopened after a
        // save, keeps what is on screen: starting over would collapse the editor and lose
        // the scroll position.
        if ((rule ? rule.id : null) !== selectedIdRef.current) setShownSteps(STEPS_PER_TICK);
        selectedIdRef.current = rule ? rule.id : null;
        setSelectedId(rule ? rule.id : null);
        setDraft(rule ? { name: rule.name, notes: rule.notes || '', definition: clone(rule.definition) } : null);
        setDirty(false);
        setErrors({});
        setTest(null);
        setFocused(null);
        focusedInputRef.current = null;
    }, []);

    // `keepId` stays selected when it still exists; otherwise the active rule opens,
    // or the built-in rule when the agent is on the router.
    const loadRules = useCallback(async (keepId) => {
        if (!agentId) return;
        setLoading(true);
        setLoadError(null);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/rules`);
            if (agentIdRef.current !== agentId) return;
            const list = res.data.data || [];
            const active = res.data.active_rule ?? null;
            setRules(list);
            setShared(res.data.shared || []);
            setActiveRule(active);
            if (res.data.vocabulary) setVocabulary(res.data.vocabulary);
            if (res.data.limits) setLimits(res.data.limits);
            if (onCatalogLoadedRef.current) onCatalogLoadedRef.current(res.data.variables || []);
            openRule(list.find((rule) => rule.id === keepId) || list.find((rule) => rule.id === active) || list[0] || null);
        } catch (err) {
            if (agentIdRef.current !== agentId) return;
            setLoadError(err.response?.data?.message || err.response?.data?.error || 'Failed to load the rules');
        } finally {
            if (agentIdRef.current === agentId) setLoading(false);
        }
    }, [agentId, openRule]);

    useEffect(() => {
        agentIdRef.current = agentId;
        setRules([]);
        setShared([]);
        setActiveRule(undefined);
        openRule(null);
        loadRules(null);
    }, [agentId, loadRules, openRule]);

    // Mount the rest of the open rule's steps a few at a time, each batch in a transition:
    // React renders it in slices between frames, so the steps already on screen stay
    // responsive to typing and scrolling while the others arrive.
    const stepCount = draft?.definition?.steps?.length || 0;
    useEffect(() => {
        if (shownSteps < stepCount) {
            startTransition(() => setShownSteps((shown) => shown + STEPS_PER_TICK));
        }
    }, [shownSteps, stepCount]);

    // ==================== EDITING ====================

    const edit = useCallback((mutate) => {
        setDraft((previous) => {
            const next = clone(previous);
            mutate(next);
            return next;
        });
        setDirty(true);
        setTest(null);      // it described the rule as it was
    }, []);

    const setField = useCallback((path, value) => {
        setDraft((previous) => ({ ...previous, definition: setIn(previous.definition, path, value) }));
        setDirty(true);
        setTest(null);      // it described the rule as it was
        const key = pathKey(path);
        setErrors((previous) => {
            if (!(key in previous)) return previous;
            const { [key]: cleared, ...rest } = previous;
            return rest;
        });
    }, []);

    // Adding, removing or moving a row shifts every index after it, so the errors and
    // the insert target (both kept by position) are dropped.
    const restructure = useCallback((mutate) => {
        edit((next) => mutate(next.definition));
        setErrors({});
        setFocused(null);
        focusedInputRef.current = null;
    }, [edit]);

    const focusFormula = useCallback((path, input) => {
        focusedInputRef.current = input;
        setFocused({ key: pathKey(path), path });
    }, []);

    useImperativeHandle(ref, () => ({
        // Called by the page's Variables panel with a variable's name.
        insertVariable: (name) => {
            const input = focusedInputRef.current;
            if (readOnly) {
                onNotify({ message: 'This rule is read-only. Duplicate it to make your own version.', severity: 'info' });
                return;
            }
            if (!focused || !input) {
                onNotify({ message: 'Click into a formula box first, then pick a value.', severity: 'info' });
                return;
            }
            const current = input.value || '';
            const start = input.selectionStart ?? current.length;
            const end = input.selectionEnd ?? current.length;
            setField(focused.path, current.slice(0, start) + name + current.slice(end));
            requestAnimationFrame(() => {
                const caret = start + name.length;
                input.focus();
                input.setSelectionRange(caret, caret);
            });
        },
    }), [readOnly, focused, onNotify, setField]);

    // ==================== REQUESTS ====================

    const failed = (err, fallback) => {
        const data = err.response?.data || {};
        if (Array.isArray(data.errors)) setErrors(errorsByPath(data.errors));
        if (data.error === 'INVALID_RULE_NAME') setErrors({ name: data.message });
        if (data.error === 'INVALID_RULE_NOTES') setErrors({ notes: data.message });
        onNotify({ message: data.message || data.error || fallback, severity: 'error' });
    };

    const uniqueName = (base) => {
        const taken = new Set(rules.map((rule) => rule.name.toLowerCase()));
        const stem = base.slice(0, limits.rule_name_chars - 6);
        if (!taken.has(stem.toLowerCase())) return stem;
        let number = 2;
        while (taken.has(`${stem} ${number}`.toLowerCase())) number += 1;
        return `${stem} ${number}`;
    };

    const selectRule = (rule) => {
        if (rule.id === selectedId) return;
        if (dirty && !window.confirm(DISCARD_CHANGES)) return;
        openRule(rule);
    };

    // New, Duplicate and "Copy here" all save a new rule of this agent and open it.
    const createRule = async (payload, message) => {
        if (dirty && !window.confirm(DISCARD_CHANGES)) return;
        setBusy(true);
        try {
            const res = await axiosInstance.post(api, payload);
            onNotify({ message, severity: 'success' });
            await loadRules(res.data.data.id);
        } catch (err) {
            failed(err, 'Failed to save the rule');
        } finally {
            setBusy(false);
        }
    };

    const handleNew = () => createRule(
        { name: uniqueName('New rule'), notes: '', definition: NEW_RULE_DEFINITION },
        'New rule created. It only holds until you change its steps.',
    );

    const handleDuplicate = (rule) => createRule(
        { name: uniqueName(`Copy of ${rule.name}`), notes: rule.is_system ? '' : (rule.notes || ''), definition: rule.definition },
        `"${rule.name}" duplicated`,
    );

    // A rule of another agent is listed by name only, so the server copies it by its id.
    const handleCopyShared = (rule) => createRule(
        { name: uniqueName(rule.name), copy_from: rule.id },
        `"${rule.name}" copied from ${rule.source_agent_name}`,
    );

    const handleSave = async () => {
        if (!selected || readOnly) return;
        setBusy(true);
        try {
            await axiosInstance.put(`${api}/${selected.id}`, {
                name: draft.name, notes: draft.notes, definition: draft.definition,
            });
            onNotify({ message: 'Rule saved', severity: 'success' });
            await loadRules(selected.id);
        } catch (err) {
            failed(err, 'Failed to save the rule');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (rule) => {
        if (!window.confirm(`Delete the rule "${rule.name}"?`)) return;
        setBusy(true);
        try {
            await axiosInstance.delete(`${api}/${rule.id}`);
            onNotify({ message: 'Rule deleted', severity: 'success' });
            await loadRules(rule.id === selectedId ? null : selectedId);
        } catch (err) {
            failed(err, 'Failed to delete the rule');
        } finally {
            setBusy(false);
        }
    };

    // Asks before a switch between the built-in router and a rule, and says what the
    // agent holds: whichever stops deciding also stops managing it. The position is
    // read from the server at that moment, so it is this agent's now and not a
    // figure left on screen.
    const confirmSwitch = async (question) => {
        let position = 'Open position: could not be read.';
        setBusy(true);
        try {
            const held = (await axiosInstance.get(api)).data.position;
            if (held) {
                position = held.side === 'NONE'
                    ? 'Open position: none.'
                    : `Open position: ${held.side} ${showValue(held.quantity)} ${agent.symbol}.`;
            }
        } catch (err) {
            // The question is still asked, with the line above.
        } finally {
            setBusy(false);
        }
        return window.confirm(`${question}\n\n${position}`);
    };

    const activate = async (ruleRef, message) => {
        setBusy(true);
        try {
            const res = await axiosInstance.post(`${api}/activate`, { rule: ruleRef });
            setActiveRule(res.data.active_rule ?? null);
            if (res.data.data) onAgentUpdated(res.data.data);
            onNotify({ message, severity: 'success' });
        } catch (err) {
            failed(err, 'Failed to activate the rule');
        } finally {
            setBusy(false);
        }
    };

    const handleActivate = async (rule) => {
        if (dirty && rule.id === selectedId) {
            onNotify({ message: 'Save your changes before activating this rule.', severity: 'warning' });
            return;
        }
        if (activeRule === null && !(await confirmSwitch(
            `Run "${agent.name}" on the rule "${rule.name}"?\n\n`
            + 'From its next cycle only this rule trades. The built-in router\'s risk exits and '
            + 'the 15:45 / 15:55 CT end-of-day sell stop for this agent.',
        ))) return;
        activate(rule.id, `"${rule.name}" is now active`);
    };

    const handleUseRouter = async () => {
        if (!(await confirmSwitch(
            `Run "${agent.name}" on the built-in Pure Code Router again?\n\n`
            + 'Its rule stops deciding. From the next cycle the router\'s own signal, its risk exits and '
            + 'the 15:45 / 15:55 CT end-of-day sell act on this agent.',
        ))) return;
        activate(ROUTER_REF, 'The built-in Pure Code Router is active');
    };

    const handleTest = async () => {
        if (!selected || !draft) return;
        const testedId = selected.id;
        setTesting(true);
        try {
            const body = selected.is_system
                ? { rule: selected.id }
                : { name: draft.name, definition: draft.definition };
            const res = await axiosInstance.post(`${api}/test`, body);
            if (agentIdRef.current !== agentId || selectedIdRef.current !== testedId) return;
            const data = res.data.data || {};
            if (data.valid === false) {
                setErrors(errorsByPath(data.errors));
                setTest(null);
                onNotify({ message: data.errors?.[0]?.message || 'This rule is not valid yet', severity: 'error' });
            } else {
                setErrors({});
                setTest({ ...data, market_closed: res.data.market_closed, as_of: res.data.as_of });
            }
        } catch (err) {
            if (agentIdRef.current === agentId && selectedIdRef.current === testedId) failed(err, 'Failed to test the rule');
        } finally {
            setTesting(false);
        }
    };

    // ==================== RULES LIST ====================

    const smallButtonSx = { fontFamily, textTransform: 'none', fontSize: '0.7rem', mt: 1, borderRadius: '6px', px: 1.5, py: 0.3 };
    const activeChip = (
        <Chip icon={<MuiIcons.CheckCircle sx={{ fontSize: '0.8rem' }} />} label="Active" size="small"
            sx={{ mt: 1, bgcolor: '#dcfce7', color: '#16a34a', fontSize: '0.7rem', fontWeight: 600, height: 22, fontFamily }} />
    );

    const renderRuleCard = (rule) => {
        const isActive = activeRule === rule.id;
        const isSelected = selectedId === rule.id;
        return (
            <Paper key={rule.id} onClick={() => selectRule(rule)}
                sx={{
                    p: 1.5, mb: 1, cursor: 'pointer', borderRadius: '8px',
                    border: `1px solid ${isSelected ? ACCENT : '#e2e8f0'}`,
                    bgcolor: isSelected ? `${ACCENT}08` : 'transparent',
                    '&:hover': { borderColor: ACCENT },
                }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, flex: 1 }}>{rule.name}</Typography>
                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        {canAdd && (
                            <Tooltip title={rule.is_system ? 'Duplicate to make your own version' : 'Duplicate rule'}>
                                <span>
                                    <IconButton size="small" disabled={busy} aria-label={`Duplicate ${rule.name}`}
                                        onClick={(e) => { e.stopPropagation(); handleDuplicate(rule); }}
                                        sx={{ p: 0.3, color: '#94a3b8', '&:hover': { color: ACCENT } }}>
                                        <MuiIcons.ContentCopy sx={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        {canDelete && !rule.is_system && !isActive && (
                            <Tooltip title="Delete rule">
                                <span>
                                    <IconButton size="small" disabled={busy} aria-label={`Delete ${rule.name}`}
                                        onClick={(e) => { e.stopPropagation(); handleDelete(rule); }}
                                        sx={{ p: 0.3, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                        <MuiIcons.DeleteOutline sx={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
                {rule.is_system && (
                    <Chip icon={<MuiIcons.Lock sx={{ fontSize: '0.75rem' }} />} label="Built-in" size="small" variant="outlined"
                        sx={{ mt: 0.5, fontSize: '0.65rem', height: 20, fontFamily }} />
                )}
                <Box>
                    {isActive ? activeChip : (
                        <Button variant="contained" size="small" disabled={!canEdit || busy}
                            startIcon={<MuiIcons.CheckCircleOutline sx={{ fontSize: '0.85rem' }} />}
                            onClick={(e) => { e.stopPropagation(); handleActivate(rule); }}
                            sx={{ ...smallButtonSx, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}>
                            Activate
                        </Button>
                    )}
                </Box>
            </Paper>
        );
    };

    const renderRulesList = () => (
        <Paper sx={{ width: 280, flexShrink: 0, p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>Rules</Typography>
                {canAdd && (
                    <Tooltip title="New rule">
                        <span>
                            <IconButton size="small" onClick={handleNew} disabled={busy || loading} aria-label="New rule">
                                <MuiIcons.Add fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>
            {loading && rules.length === 0 ? <Skeleton variant="rectangular" height={100} /> : rules.map(renderRuleCard)}

            {/* Rules written in this owner's other No-LLM agents; using one copies it in. */}
            {shared.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#64748b', mb: 1 }}>
                        From your other agents
                    </Typography>
                    {shared.map((rule) => (
                        <Paper key={`shared-${rule.id}`} sx={{ p: 1.5, mb: 1, borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600 }}>{rule.name}</Typography>
                            <Chip label={rule.source_agent_name} size="small" variant="outlined"
                                icon={<MuiIcons.SmartToy sx={{ fontSize: '0.8rem' }} />}
                                sx={{ mt: 0.5, fontSize: '0.65rem', height: 20, maxWidth: '100%' }} />
                            <Box>
                                <Button variant="outlined" size="small" disabled={!canAdd || busy}
                                    startIcon={<MuiIcons.ContentCopy sx={{ fontSize: '0.85rem' }} />}
                                    onClick={() => handleCopyShared(rule)}
                                    sx={{ ...smallButtonSx, color: '#16a34a', borderColor: '#22c55e' }}>
                                    Copy here
                                </Button>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}

            {/* The built-in router is not a rule, so it has no card to open. */}
            {activeRule !== undefined && (
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#64748b' }}>
                        Built-in Pure Code Router
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', mt: 0.5 }}>
                        Trades on the platform&apos;s own signal, with its risk exits and end-of-day sell. Its logic is fixed in code.
                    </Typography>
                    {activeRule === null ? activeChip : (
                        <Button variant="outlined" size="small" disabled={!canEdit || busy} onClick={handleUseRouter}
                            sx={{ ...smallButtonSx, color: '#475569', borderColor: '#cbd5e1' }}>
                            Use the built-in router
                        </Button>
                    )}
                </Box>
            )}
        </Paper>
    );

    // ==================== STEP EDITOR ====================

    const formulaProps = (path, extra = {}) => ({
        value: path.reduce((target, part) => (target == null ? '' : target[part]), draft.definition) ?? '',
        error: errors[pathKey(path)],
        path,
        readOnly,
        focused: focused?.key === pathKey(path),
        maxLength: limits.formula_chars,
        onEdit: setField,
        onFocusField: focusFormula,
        ...extra,
    });

    const renderTestSummary = () => {
        if (!test) return null;
        const decision = test.decision || {};
        const trades = decision.action && decision.action !== 'HOLD';
        const stepNumber = test.decided_step === null || test.decided_step === undefined ? null : test.decided_step + 1;
        let headline = 'HOLD';
        if (trades && (decision.quantity === null || decision.quantity === undefined)) {
            headline = `${ACTION_LABELS[decision.action] || decision.action}: close the whole position at the market`;
        } else if (trades) {
            headline = `${ACTION_LABELS[decision.action] || decision.action} ${showValue(decision.quantity)}`
                + (decision.price === null || decision.price === undefined ? ' at the market' : ` @ ${showValue(decision.price)}`);
        }
        return (
            <Alert severity={trades ? 'success' : 'info'} onClose={() => setTest(null)}
                sx={{ fontFamily, fontSize: '0.8rem', mb: 1.5, '& .MuiAlert-message': { minWidth: 0 } }}>
                <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600 }}>
                    On the live data{test.as_of ? ` of ${test.as_of}` : ''} this rule would: {headline}
                    {stepNumber ? ` (step ${stepNumber})` : ''}
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#475569', mt: 0.5, wordBreak: 'break-word' }}>
                    {decision.reasoning}
                </Typography>
                {test.market_closed && (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#b45309', mt: 0.5 }}>
                        A real cycle would hold right now: {test.market_closed}.
                    </Typography>
                )}
                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', mt: 0.5 }}>
                    Nothing was stored and no order was sent.
                </Typography>
            </Alert>
        );
    };

    const renderValues = () => {
        const values = draft.definition.values || [];
        return (
            <Box sx={{ mb: 2 }}>
                <Typography sx={sectionTitleSx}>Calculated values</Typography>
                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', mb: 1 }}>
                    Named formulas the steps can use. A value can use the ones above it.
                </Typography>
                {errors.values && <Alert severity="error" sx={{ fontFamily, fontSize: '0.75rem', mb: 1 }}>{errors.values}</Alert>}
                {values.map((item, index) => {
                    const tested = test?.values?.[index];
                    return (
                        // Keyed by position: a row has no id, and rows are only added, removed or moved whole.
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                            <FormulaField label="Name" placeholder="alert_level"
                                {...formulaProps(['values', index, 'name'], { maxLength: 40, singleLine: true })} width={180} />
                            <Typography sx={{ fontFamily: 'monospace', mt: 1, color: '#94a3b8' }}>=</Typography>
                            <FormulaField label="Formula" placeholder="equity_avg_cost + current_atr"
                                {...formulaProps(['values', index, 'formula'])}
                                hint={tested ? `= ${showValue(tested.value)}` : undefined} flex={1} />
                            {!readOnly && (
                                <Tooltip title="Remove value">
                                    <IconButton size="small" aria-label={`Remove value ${index + 1}`} sx={rowIconSx}
                                        onClick={() => restructure((definition) => { definition.values.splice(index, 1); })}>
                                        <MuiIcons.Close sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    );
                })}
                {values.length === 0 && readOnly && (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>None.</Typography>
                )}
                {!readOnly && (
                    <Button size="small" startIcon={<MuiIcons.Add sx={{ fontSize: '0.9rem' }} />} sx={addButtonSx}
                        disabled={values.length >= limits.values}
                        onClick={() => restructure((definition) => {
                            definition.values = [...(definition.values || []), { name: '', formula: '' }];
                        })}>
                        Add value
                    </Button>
                )}
            </Box>
        );
    };

    const renderEditor = () => {
        if (loadError) return <Alert severity="error" sx={{ fontFamily }}>{loadError}</Alert>;
        if (!selected || !draft) {
            return loading
                ? <Skeleton variant="rectangular" height={160} />
                : <Alert severity="info" sx={{ fontFamily }}>Select a rule from the left panel.</Alert>;
        }
        const steps = draft.definition.steps || [];
        const general = [errors[''], errors.steps].filter(Boolean);
        return (
            <>
                {selected.is_system && (
                    <Alert severity="info" icon={<MuiIcons.Lock />} sx={{ fontFamily, fontSize: '0.8rem', mb: 1.5 }}>
                        This is the built-in rule. It cannot be changed or deleted. Duplicate it to make your own version.
                    </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <TextField size="small" label="Rule Name" value={draft.name}
                        error={Boolean(errors.name)} helperText={errors.name}
                        onChange={(e) => {
                            const name = e.target.value;
                            setDraft((previous) => ({ ...previous, name }));
                            setDirty(true);
                            // The same object unless there is an error to clear: every step
                            // takes `errors` as a prop, and a new one re-renders them all.
                            setErrors((previous) => {
                                if (!previous.name) return previous;
                                const { name: cleared, ...rest } = previous;
                                return rest;
                            });
                        }}
                        InputProps={{ readOnly }} inputProps={{ maxLength: limits.rule_name_chars }}
                        sx={{ ...fieldSx, flex: 2, minWidth: 260 }} />
                    <TextField size="small" label="Notes" value={draft.notes}
                        error={Boolean(errors.notes)} helperText={errors.notes}
                        onChange={(e) => {
                            const notes = e.target.value;
                            setDraft((previous) => ({ ...previous, notes }));
                            setDirty(true);
                            // As for the name: the same object unless there is an error to clear.
                            setErrors((previous) => {
                                if (!previous.notes) return previous;
                                const { notes: cleared, ...rest } = previous;
                                return rest;
                            });
                        }}
                        InputProps={{ readOnly }} inputProps={{ maxLength: limits.notes_chars }}
                        sx={{ ...fieldSx, flex: 3, minWidth: 260 }} />
                    <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                    <Button variant="outlined" size="small" onClick={handleTest} disabled={testing || busy}
                        startIcon={testing ? <CircularProgress size={14} /> : <MuiIcons.PlayArrow sx={{ fontSize: '1rem' }} />}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#8b5cf6', color: '#8b5cf6', height: 37, '&:hover': { borderColor: '#7c3aed', bgcolor: '#8b5cf608' } }}>
                        Test on live data
                    </Button>
                    {selected.is_system ? (
                        <Button variant="contained" onClick={() => handleDuplicate(selected)} disabled={!canAdd || busy}
                            startIcon={<MuiIcons.ContentCopy sx={{ fontSize: '1rem' }} />}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, height: 37, '&:hover': { bgcolor: '#d97706' } }}>
                            Duplicate to edit
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={handleSave} disabled={readOnly || busy || !dirty}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, height: 37, '&:hover': { bgcolor: '#d97706' } }}>
                            {dirty ? 'Save' : 'Saved'}
                        </Button>
                    )}
                    </Box>
                </Box>
                {general.map((message) => (
                    <Alert key={message} severity="error" sx={{ fontFamily, fontSize: '0.8rem', mb: 1.5 }}>{message}</Alert>
                ))}
                {renderTestSummary()}
                {renderValues()}

                <Typography sx={sectionTitleSx}>Steps</Typography>
                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', mb: 1 }}>
                    Checked top to bottom on every cycle. The first step whose conditions all pass decides; if none does,
                    the agent holds. A value that is N/A never passes a condition. One Buy or Short order can be worth
                    at most the agent&apos;s Capital Allocation. A Buy or Sell waits while an order on its side is still
                    working; Close All and Emergency Exit send what no working order is already closing.
                    Functions: {vocabulary.functions.join(', ')}.
                </Typography>
                {steps.slice(0, shownSteps).map((step, index) => (
                    // Keyed by position: a step has no id, and steps are only added, removed or moved whole.
                    <StepCard key={index} step={step} index={index} count={steps.length} errors={errors}
                        tested={test?.steps?.[index]} readOnly={readOnly}
                        focusedKey={focused && focused.key.startsWith(`steps[${index}].`) ? focused.key : null}
                        limits={limits} vocabulary={vocabulary}
                        onField={setField} onRestructure={restructure} onFocusField={focusFormula} />
                ))}
                {shownSteps < steps.length && (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={12} sx={{ color: '#94a3b8' }} />
                        Showing {shownSteps} of {steps.length} steps
                    </Typography>
                )}
                {!readOnly && shownSteps >= steps.length && (
                    <Button size="small" startIcon={<MuiIcons.Add sx={{ fontSize: '0.9rem' }} />} sx={addButtonSx}
                        disabled={steps.length >= limits.steps}
                        onClick={() => restructure((definition) => {
                            definition.steps = [...(definition.steps || []), newStep((definition.steps || []).length + 1)];
                        })}>
                        Add step
                    </Button>
                )}
            </>
        );
    };

    const activeMissing = activeRule !== undefined && activeRule !== null && !loading
        && !rules.some((rule) => rule.id === activeRule);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minWidth: 0, minHeight: 0 }}>
            {activeRule === null && (
                <Alert severity="warning" sx={{ fontFamily, fontSize: '0.82rem' }}>
                    This agent runs on the <strong>built-in Pure Code Router</strong>, so none of the rules below is in use.
                    Activate one to run the agent on it: from then on only the rule trades, and the router&apos;s risk
                    exits and end-of-day sell stop for this agent.
                </Alert>
            )}
            {activeMissing && (
                <Alert severity="error" sx={{ fontFamily, fontSize: '0.82rem' }}>
                    This agent&apos;s active rule no longer exists, so every cycle holds. Activate a rule.
                </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
                {renderRulesList()}
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', pr: 0.5, pt: 0.75 }}>
                    {renderEditor()}
                </Box>
            </Box>
        </Box>
    );
});

AIAgentRuleEditor.displayName = 'AIAgentRuleEditor';

export default AIAgentRuleEditor;
